/**
 * Search in the transcript panel: the matches of a phrase across the whole transcript (a long meeting's later pages are
 * fetched first: realtime/bootstrap.ts allSegments), case and accents ignored ("jeylu" finds "Jeylú", "COUNCIL" finds
 * "council"). The panel (TranscriptPanel.tsx) shows "3 of 12" with previous / next, marks the matches in the text and
 * scrolls to the current one; ?q= in the page's address opens it with that phrase (main.tsx), so a search result on
 * /meetings links straight to the words in the meeting.
 *
 * The open search and its phrase are a tiny store of their own, so typing re-renders the search row alone; the marks
 * are drawn on the page with the CSS Custom Highlight API (no re-render of thousands of lines).
 */
import { useSyncExternalStore } from "react";
import type { Seconds, TranscriptSegment } from "../../types";

/** Lower case without accents, one character for one (so offsets in the folded text are offsets in the original). */
export function fold(s: string): string {
  let out = "";
  for (const ch of s) {
    const f = ch.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase();
    out += f.length === ch.length ? f : ch.toLowerCase().length === ch.length ? ch.toLowerCase() : ch;
  }
  return out;
}

/** A segment's text as the panel shows it (with word timings, the words joined by spaces). */
export const textOf = (seg: TranscriptSegment) => (seg.words?.length ? seg.words.map((w) => w.w).join(" ") : seg.text);

export interface Match { segId: string; index: number; from: number; to: number; t: Seconds }

/** Every occurrence of the phrase (2 characters or more), in transcript order, with the second it's said at. */
export function findMatches(segments: TranscriptSegment[], query: string): Match[] {
  const q = fold(query.replace(/\s+/g, " ").trim());
  if (q.length < 2) return [];
  const out: Match[] = [];
  segments.forEach((seg, index) => {
    const text = fold(textOf(seg));
    for (let from = text.indexOf(q); from >= 0; from = text.indexOf(q, from + q.length)) {
      let t = seg.start;
      if (seg.words?.length) {
        let pos = 0;
        for (const w of seg.words) { if (pos + w.w.length > from) { t = w.start; break; } pos += w.w.length + 1; }
      }
      out.push({ segId: seg.id, index, from, to: from + q.length, t });
    }
  });
  return out;
}

/** The match to start at: the first at or after a second (?t= with ?q=), else the first. */
export const matchFrom = (matches: Match[], t: Seconds | null) => (t == null ? 0 : Math.max(0, matches.findIndex((m) => m.t >= t - 1)));

// ---------- the open search ----------
export interface SearchState { open: boolean; query: string; startAt: Seconds | null }
let state: SearchState = { open: false, query: "", startAt: null };
const listeners = new Set<() => void>();
const set = (next: Partial<SearchState>) => { state = { ...state, ...next }; listeners.forEach((l) => l()); };

export const transcriptSearch = {
  get: () => state,
  /** Open the search, with a phrase (?q=) and the moment to start from (?t=). */
  open: (query = state.query, startAt: Seconds | null = null) => set({ open: true, query, startAt }),
  setQuery: (query: string) => set({ query, startAt: null }),
  close: () => set({ open: false, query: "", startAt: null }),
  subscribe: (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; },
};
export const useTranscriptSearch = () => useSyncExternalStore(transcriptSearch.subscribe, transcriptSearch.get, transcriptSearch.get);
